import { IItemSection, SectionType } from '../itemSection';
import { Product, ProductType } from '@/store/modules/product';
import { ElementType } from '../element';
import { IShader } from '../shaders/shader';
import { ItemParams } from '../itemParams';
import { stringUtil } from '../stringUtil';
import store from '@/store';
import { ItemSectionOption } from '../itemSectionOption';
import { handles } from '../handles';
import { Legs } from '../legs/legs';
import { Triple } from '../triple';
import { UnifyWoodColorsCommand } from '../command/unifyWoodColorsCommand';

export abstract class BaseSectionCreator {

    protected abstract readonly sections: IItemSection[];

    protected colorExclusions: Array<Triple<string, string[], string[]>> = [];
    protected legsExclusions: Array<Triple<string, string[], string[]>> = [];
    protected handlesExclusions: Array<Triple<string, string[], string[]>> = [];

    constructor(
        protected scenePreview: any,
    ) {
        this.createExclusions();
    }

    public abstract create(product: Product): IItemSection[];
    protected abstract applyLanguage(): void;

    protected updateShaderSection(
        sectionType: SectionType,
        elementType: ElementType,
        shaders: IShader[],
        onClick: ((shader: IShader) => void) | null = null) {
        const section = this.getSectionByType(sectionType);
        section.params = [];
        shaders.forEach((shader) => {
            const param = this.createParam(
                shader,
                shader.avatar,
                () => {
                    if (this.unifyChange(shader, sectionType)) { return; }
                    if (onClick !== null) {
                        onClick(shader);
                    } else {
                        this.scenePreview.changeColors(elementType, shader);
                    }
                },
                () => {
                    let selectedShader!: IShader;
                    if (elementType === ElementType.BODY) {
                        selectedShader = store.getters.shaders.selectedBody;
                    }
                    if (elementType === ElementType.INSIDE) {
                        selectedShader = store.getters.shaders.selectedShelf;
                    }
                    if (elementType === ElementType.WALL) {
                        selectedShader = store.getters.shaders.selectedWall;
                    }
                    return shader === selectedShader;
                },
                () => {
                    // if (!(elementType === ElementType.BODY)) { return false; }
                    // const frontShader = store.getters.shaders.selectedFront;
                    // return this.woodShadersExclusions.some((exclusion) =>
                    //     exclusion.x.includes(frontShader.key) &&
                    //     exclusion.y.includes(shader.key),
                    // );
                    // return false;
                    return this.isFurnitureParamDisabled(shader, sectionType);
                },
            );
            section.params.push(param);
        });
    }

    protected updateFrontsSection(shaders: IShader[]) {
        const section = this.getSectionByType(SectionType.FRONT_COLOR);
        const colorAllOption = new ItemSectionOption('colorAll');
        const colorSelectedOption = new ItemSectionOption('colorSelected');
        section.params = [];
        shaders.forEach((shader) => {
            const param = this.createParam(
                shader,
                shader.avatar,
                () => {
                    if (this.unifyChange(shader, SectionType.FRONT_COLOR, colorSelectedOption.isSelected)) { return; }
                    this.scenePreview.changeColors(ElementType.FRONT, shader, colorSelectedOption.isSelected)
                },
                () => {
                    if (this.scenePreview.selectedElements.length > 0 &&
                        this.scenePreview.selectedElements[0].type === ElementType.FRONT) {
                        return shader.key === this.scenePreview.selectedElements[0].shaderName;
                    }
                    return shader === store.getters.shaders.selectedFront;
                },
                () => {
                    // return false;
                    return this.isFurnitureParamDisabled(shader, SectionType.FRONT_COLOR);
                },
            );
            section.params.push(param);
        });
        section.options = [colorAllOption, colorSelectedOption];
    }

    protected updateLegsSection(product: Product, legs: Legs[]) {
        const section = this.getSectionByType(SectionType.LEGS);
        section.params = [];
        if (product.depth === 255 && product.type === ProductType.CHEST_FURNITURE) {
            section.visible = false;
        } else {
            legs.forEach((x) => {
                const param = this.createParam(
                    x.shader,
                    x.avatar,
                    () => {
                        if (this.unifyChange(x.shader, SectionType.LEGS)) { return; }
                        this.scenePreview.changeLegs(x)
                    },
                    () => x === store.getters.shaders.selectedLegs,
                    () => {
                        // return false;
                        const handlesShader = store.getters.shaders.selectedHandle.shader;
                        return this.isElementParamDisabled(x.shader, this.legsExclusions, handlesShader);
                    },
                );
                section.params.push(param);
            });
        }
    }

    protected updateHandlesSection() {
        const section = this.getSectionByType(SectionType.HANDLES);
        section.params = [];
        handles.forEach((x) => {
            const param = this.createParam(
                x.shader,
                x.avatar,
                () => {
                    if (this.unifyChange(x.shader, SectionType.HANDLES)) { return; }
                    this.scenePreview.changeHandles(x);
                },
                () => x === store.getters.shaders.selectedHandle,
                () => {
                    // return false;
                    const legsShader = store.getters.shaders.selectedLegs.shader;
                    return this.isElementParamDisabled(x.shader, this.handlesExclusions, legsShader);
                },
            );
            section.params.push(param);
        });
    }

    protected createParam(
        shader: IShader,
        avatar: string,
        callback: () => void,
        isSelected: () => boolean = () => true,
        isDisabled: () => boolean = () => false): ItemParams {
        const isHex = stringUtil.isHex(avatar);
        const color = isHex ? avatar : '';
        const url = isHex ? '' : avatar;
        const param = new ItemParams(color, url, shader.text, callback, isSelected, isDisabled);
        return param;
    }

    protected hideLegsSection() {
        this.hide(SectionType.LEGS);
    }

    protected hide(sectionType: SectionType) {
        const section = this.getSectionByType(sectionType);
        section.visible = false;
    }

    protected createExclusions() {
        // x -> color, y -> legs, z -> handles
        this.colorExclusions.push(new Triple('ShaderH3396',
            ['ShaderNStBox', 'ShaderNStozek', 'ShaderNWalec', 'ShaderNBox'],
            ['ShaderU01Siro', 'ShaderU06Siro']));
        this.colorExclusions.push(new Triple('ShaderH3162',
            ['ShaderNStScieta', 'ShaderNStBox', 'ShaderNStozek', 'ShaderNWalec'],
            ['ShaderU01Siro', 'ShaderU06Siro', 'ShaderU09Siro']));
        this.colorExclusions.push(new Triple('ShaderH3325',
            ['ShaderNStScieta', 'ShaderNWalec', 'ShaderNBox'],
            ['ShaderU09Siro', 'ShaderU06Siro']));
        this.colorExclusions.push(new Triple('ShaderH3157',
            ['ShaderNStScieta', 'ShaderNStBox', 'ShaderNStozek', 'ShaderNBox'],
            ['ShaderU09Siro', 'ShaderU01Siro']));


        // x -> legs, y -> color, z -> handles
        this.legsExclusions.push(new Triple('ShaderNStScieta',
            ['ShaderH3162', 'ShaderH3325', 'ShaderH3157'],
            ['ShaderU01Siro', 'ShaderU06Siro']));
        this.legsExclusions.push(new Triple('ShaderNStBox',
            ['ShaderH3396', 'ShaderH3162', 'ShaderH3157'],
            ['ShaderU09Siro', 'ShaderU06Siro']));
        this.legsExclusions.push(new Triple('ShaderNStozek',
            ['ShaderH3396', 'ShaderH3162', 'ShaderH3157'],
            ['ShaderU09Siro', 'ShaderU06Siro']));
        this.legsExclusions.push(new Triple('ShaderNWalec',
            ['ShaderH3396', 'ShaderH3162', 'ShaderH3325'],
            ['ShaderU09Siro', 'ShaderU01Siro']));
        this.legsExclusions.push(new Triple('ShaderNBox',
            ['ShaderH3396', 'ShaderH3325', 'ShaderH3157'],
            ['ShaderU01Siro', 'ShaderU06Siro', 'ShaderU09Siro']));

        // x-> handles, y -> color, z -> legs
        this.handlesExclusions.push(new Triple('ShaderU09Siro',
            ['ShaderH3162', 'ShaderH3325', 'ShaderH3157'],
            ['ShaderNStBox', 'ShaderNStozek', 'ShaderNWalec', 'ShaderNBox']));
        this.handlesExclusions.push(new Triple('ShaderU01Siro',
            ['ShaderH3396', 'ShaderH3162', 'ShaderH3157'],
            ['ShaderNStScieta', 'ShaderNWalec', 'ShaderNBox']));
        this.handlesExclusions.push(new Triple('ShaderU06Siro',
            ['ShaderH3396', 'ShaderH3162', 'ShaderH3325'],
            ['ShaderNStScieta', 'ShaderNStBox', 'ShaderNStozek', 'ShaderNBox']));
    }

    protected getSectionByType(type: SectionType): IItemSection {
        return Array.from(this.sections.values()).find((x) => x.type === type)!;
    }

    private unifyChange(
        shader: IShader,
        sectionType: SectionType,
        onlySelected: boolean = false): boolean {
        const isWoodShader = store.getters.isWoodShader(shader);
        if (isWoodShader) {
            this.scenePreview.loadNewSceneState(() => {
                const command = new UnifyWoodColorsCommand(
                    this.scenePreview,
                    sectionType,
                    shader,
                    onlySelected,
                );
                this.scenePreview.addCommand(command);
            });
            return true;
        }
        return false;
    }

    private isFurnitureParamDisabled(shader: IShader, type: SectionType): boolean {
        const isWoodShader = store.getters.isWoodShader(shader);
        if (!isWoodShader) { return false; }
        const exclusion = this.colorExclusions.find((e) => e.x === shader.key)!;
        if (!exclusion) { return false; }
        const legsShader = store.getters.shaders.selectedLegs.shader;
        if (exclusion.y.includes(legsShader.key)) { return true; }
        const handleShader = store.getters.shaders.selectedHandle.shader;
        if (exclusion.z.includes(handleShader.key)) { return true; }
        const selectedWoodShadersCount = store.getters.selectedWoodShadersCount;
        if (
            selectedWoodShadersCount === 1 &&
            store.getters.isWoodShaderSelected(type)) {
            return false;
        }
        const selectedWoodShader = store.getters.selectedWoodShader;
        if (
            selectedWoodShader &&
            selectedWoodShader.key !== shader.key) {
            return true;
        }
        return false;
    }

    private isElementParamDisabled(
        shader: IShader,
        exclusions: Array<Triple<string, string[], string[]>>,
        oppositeShader: IShader): boolean {
        const exclusion = exclusions.find((e) => e.x === shader.key);
        if (!exclusion) { return false; }
        if (exclusion.z.includes(oppositeShader.key)) { return true; }
        const selectedWoodShader = store.getters.selectedWoodShader;
        if (selectedWoodShader && exclusion.y.includes(selectedWoodShader.key)) {
            return true;
        }
        return false;
    }
}
